import React, { useCallback, useContext, useMemo } from "react";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { ClavaContext, DropDownContext } from "../../../config/contexts";
import { useStableId } from "../../../hooks/useStableId";
import { useDropdownOverlay, useDropdownToggle } from "../../../hooks/useDropdownToggle";
import { useSelectedLabel, useSelectorVisuals, useStableOnChange } from "../../../hooks/useSelectorVisuals";
import GridDropdownOverlay from "./components/GridDropdownOverlay";
import SelectorDropdownLayout from "./components/SelectorDropdownLayout";
import { GridRectangleItem } from "../GridRectangle";

export interface GridSelectorItem<T extends string | number> {
  key: T;
  label: string;
}

export interface GridSelectorProps<T extends string | number> {
  idPrefix: string;
  items: GridSelectorItem<T>[];
  onChange: (val: T | undefined) => void;
  value?: T;
  placeholder?: string;
  disabled?: boolean;
  withoutChoose?: boolean;
  label?: string;
  iconL?: FontAwesomeIconProps["icon"];
  transparent?: boolean;
  primary?: boolean;
  className?: string;
  containerClassName?: string;
  autoOpen?: boolean;
  onContainerClick?: () => void;
  dropdownMinWidth?: number;
  dropdownMaxWidth?: number;
  dropdownFitContent?: boolean;
}

export function GridSelector<T extends string | number>({
                                                          idPrefix,
                                                          onChange,
                                                          value,
                                                          placeholder,
                                                          disabled,
                                                          withoutChoose,
                                                          label,
                                                          primary,
                                                          iconL,
                                                          transparent,
                                                          items,
                                                          className,
                                                          containerClassName,
                                                          autoOpen,
                                                          onContainerClick,
                                                          dropdownMinWidth,
                                                          dropdownMaxWidth,
                                                          dropdownFitContent
                                                        }: GridSelectorProps<T>) {
  const { l } = useContext(ClavaContext);
  const { setOpen, setClose } = React.useContext(DropDownContext);
  const dropdownId = useStableId(idPrefix);

  const { realPlaceholder, inputClass } = useSelectorVisuals({
    placeholder,
    withoutChoose,
    language: l,
    transparent,
    value,
    primary,
    disabled,
    iconL,
    className
  });

  const realValue = useSelectedLabel(items, value);

  const onChangeRef = useStableOnChange(onChange);

  const {
    containerRef,
    rect,
    setRect,
    onOpen,
    onContainerClickHandler,
    onContainerKeyDownHandler
  } = useDropdownToggle(dropdownId, disabled, onContainerClick, autoOpen);

  const gridItems = useMemo((): GridRectangleItem[] => items.map((item) => ({
    id: item.key,
    label: item.label,
    onClick: () => {
      onChangeRef.current(item.key);
      setRect(false);
    },
    isSelected: item.key === value
  })), [items, value, setRect, onChangeRef]);

  const overlayRenderer = useCallback(
    (style: React.CSSProperties) => (
      <GridDropdownOverlay style={style} items={gridItems} />
    ),
    [gridItems]
  );

  const computedDropdownWidth = useMemo(() => {
    if (!dropdownFitContent) return undefined;
    if (items.length === 0) return undefined;
    const baseWidth = typeof rect === "object" && rect ? rect.width : dropdownMinWidth;
    const longestLabel = items.reduce((max, item) => Math.max(max, item.label.length), 0);
    if (!longestLabel) return undefined;
    const estimatedWidth = 48 + longestLabel * 9;
    if (baseWidth && estimatedWidth <= baseWidth) return undefined;
    const withBase = baseWidth ? Math.max(baseWidth, estimatedWidth) : estimatedWidth;
    return dropdownMaxWidth ? Math.min(withBase, dropdownMaxWidth) : withBase;
  }, [dropdownFitContent, dropdownMinWidth, dropdownMaxWidth, items, rect]);

  useDropdownOverlay(rect, dropdownId, overlayRenderer, setOpen, setClose, {
    minWidth: dropdownMinWidth,
    maxWidth: dropdownMaxWidth,
    width: computedDropdownWidth
  });

  const containerProps = useMemo(() => {
    if (!onContainerClick) return undefined;
    return {
      onClick: onContainerClickHandler,
      onKeyDown: onContainerKeyDownHandler,
      role: "button",
      tabIndex: 0
    };
  }, [onContainerClick, onContainerClickHandler, onContainerKeyDownHandler]);

  return (
    <SelectorDropdownLayout
      dropdownId={dropdownId}
      containerRef={containerRef}
      label={label}
      containerClassName={containerClassName}
      transparent={transparent}
      inputClass={inputClass}
      rect={rect}
      displayValue={realValue}
      placeholder={realPlaceholder}
      iconL={iconL}
      primary={primary}
      onOpen={onOpen}
      containerProps={containerProps}
    />
  );
}


